/**
 * The player column — the bar above the video, and the video (or the poster
 * that starts it, or the loading and empty states that stand in for both).
 *
 * Presentational: every decision is made in `StreamPanel` and arrives as a
 * prop. Split out of that file, which was past the 400-line ceiling; the split
 * runs along the seam the markup already had, `hcs-player-col`.
 */
import React from "react";
import { FaCircle, FaPlay, FaExpand, FaCompress } from "react-icons/fa";
import { StreamInlinePlayer, type InlinePlayerSource } from "../StreamInlinePlayer";
import { formatViews, timeAgo, platformLabel, type StreamingSource } from "../shared";
import type { HeroFallback } from "./useHeroFallback";
import type { FullscreenRow } from "./useFullscreenRow";

export interface StreamPanelColumnProps {
    isLive: boolean;
    isShowingClip: boolean;
    hasStream: boolean;
    /** The live platform, already narrowed — absent when there is not exactly one. */
    effectiveSource?: StreamingSource;
    /** External "watch on the platform" link, when there is one. */
    livePopoutUrl?: string | null;
    fullscreen: FullscreenRow;
    isLoading: boolean;
    hasNoPlayableContent: boolean;
    isPlaying: boolean;
    inlineSource: InlinePlayerSource | null;
    onPlay: () => void;
    posterUrl?: string;
    posterTitle: string;
    /** " · Twitch", or empty when no single platform can be named. */
    posterSourceSuffix: string;
    heroFallback: HeroFallback | null;
    hubName?: string;
}

export const StreamPanelColumn: React.FC<StreamPanelColumnProps> = ({
    isLive, isShowingClip, hasStream, effectiveSource, livePopoutUrl, fullscreen,
    isLoading, hasNoPlayableContent, isPlaying, inlineSource, onPlay,
    posterUrl, posterTitle, posterSourceSuffix, heroFallback, hubName,
}) =>
    <div className="hcs-player-col">
        {/*
          * Info-box bar ABOVE the player. The LIVE badge + stream controls
          * (fullscreen) live here in normal flow rather than absolutely over
          * the video, so they never cover the gameplay the viewer came to
          * watch.
          */}
        {isLive && hasStream &&
            <div className="hcs-player-bar">
                <div className="hcs-player__live-overlay">
                    <FaCircle className="hcs-player__live-overlay-dot" />
                    <span className="hcs-player__live-overlay-text">LIVE</span>
                    {effectiveSource &&
                        <span className="hcs-player__live-overlay-platform">on {platformLabel(effectiveSource)}</span>
                    }
                </div>
                {/* Open-on-platform link — relocated here from the video overlay. */}
                {livePopoutUrl &&
                    <a
                        className="hcs-player-bar__platform"
                        href={livePopoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        /*
                         * "Open externally" when there is no single platform to
                         * name — same wording StreamTheater uses for that case.
                         */
                        title={effectiveSource ? `Open on ${platformLabel(effectiveSource)}` : "Open externally"}
                    >
                        <span aria-hidden>↗</span> {effectiveSource ? platformLabel(effectiveSource) : "Open"}
                    </a>
                }
                {/*
                  * Our own fullscreen control (NOT the embed's) — fullscreens
                  * the player+chat row so chat stays. Hidden where the
                  * Fullscreen API is unavailable (iOS Safari) so we never show
                  * a control that can't work — and never call into a missing API.
                  */}
                {fullscreen.supported &&
                    <button
                        type="button"
                        className="hcs-player-bar__fs"
                        onClick={fullscreen.toggle}
                        aria-label={fullscreen.isFullscreen ? "Exit fullscreen" : "Fullscreen with chat"}
                        title={fullscreen.isFullscreen ? "Exit fullscreen" : "Fullscreen with chat"}
                    >
                        {fullscreen.isFullscreen ? <FaCompress /> : <FaExpand />}
                        <span>{fullscreen.isFullscreen ? "Exit" : "Fullscreen"}</span>
                    </button>
                }
            </div>
        }
        <div className="hcs-player">
            {isLoading
                ? <div className="hcs-player__loading" aria-label="Loading stream content" />
                : hasNoPlayableContent
                    ? <div className="hcs-player__no-content">
                        <FaPlay className="hcs-player__no-content-icon" />
                        <div className="hcs-player__no-content-title">No streams yet</div>
                        <div className="hcs-player__no-content-sub">
                            When {hubName ?? "this creator"} goes live or posts a VOD, it appears here.
                        </div>
                    </div>
                    : isPlaying && inlineSource
                        ? <StreamInlinePlayer source={inlineSource} />
                        : <button type="button" className="hcs-player__fallback" onClick={onPlay}
                            disabled={!inlineSource}>
                            {posterUrl
                                ? <img src={posterUrl} alt={posterTitle} loading="lazy" />
                                : <div className="hcs-player__empty-bg" />}
                            <div className="hcs-player__fallback-overlay">
                                <div className="hcs-player__fallback-banner">
                                    {isLive
                                        ? <><FaCircle className="hcs-player__live-dot" /> LIVE NOW{posterSourceSuffix}</>
                                        : isShowingClip
                                            ? <>CLIP{posterSourceSuffix}</>
                                            : <>FEATURED{posterSourceSuffix}</>}
                                </div>
                                <div className="hcs-player__fallback-play"><FaPlay /></div>
                                <div className="hcs-player__fallback-meta">
                                    <div className="hcs-player__fallback-title">{posterTitle}</div>
                                    {heroFallback && !isLive &&
                                        <div className="hcs-player__fallback-sub">
                                            {heroFallback.viewCount ? `${formatViews(heroFallback.viewCount)} views · ` : ""}
                                            {timeAgo(heroFallback.when)}
                                        </div>
                                    }
                                    {isLive &&
                                        <div className="hcs-player__fallback-sub">Click to start watching</div>
                                    }
                                </div>
                            </div>
                        </button>
            }
        </div>
    </div>;

